import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import { FileText } from 'lucide-react';
import SearchableSelect from '@/temu-platform/modules/products/components/SearchableSelect';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { showToast } from '@/utils/toast';

const ACCEPT_FILES = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const MAX_FILE_BYTES = 20 * 1024 * 1024;

function parseTypeOptions(res) {
  const list = res?.certificateTypeInfoList
    || res?.data?.certificateTypeInfoList
    || res?.info?.certificateTypeInfoList
    || [];
  return Array.isArray(list) ? list.filter((item) => Number(item?.certificateTypeId) !== 844) : [];
}

function parseNeedUploadItems(resOrType) {
  const list = resOrType?.needUploadItems
    || resOrType?.result?.needUploadItems
    || resOrType?.item?.needUploadItems
    || resOrType?.data?.needUploadItems
    || [];
  return Array.isArray(list) ? list : [];
}

function typeOptionLabel(item) {
  return item?.certificateType || item?.certificateTypeName || String(item?.certificateTypeId || '');
}

function typeCodeOf(item) {
  return item?.certificateTypeCode || item?.certificateTypeCodes || item?.typeCode || '';
}

function isImageNeedItem(item) {
  const type = Number(item?.itemType);
  if (type === 2) return true;
  const name = String(item?.itemName || item?.name || '').toLowerCase();
  return /图|实拍|photo|image|pic|label/.test(name);
}

function isAllowedCertFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const okExt = name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
  const type = String(file.type || '').toLowerCase();
  const okType = !type
    || type === 'application/pdf'
    || type === 'image/png'
    || type === 'image/jpeg'
    || type === 'image/jpg';
  return okExt && okType;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const idx = text.indexOf(',');
      resolve(idx >= 0 ? text.slice(idx + 1) : text);
    };
    reader.onerror = () => reject(new Error(i18n.t('certForm.readFailed', { ns: 'workbench' })));
    reader.readAsDataURL(file);
  });
}

function unwrapApiData(res) {
  if (res?.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
    return res.data;
  }
  return res || {};
}

function extractCertificateExt(nameOrUrl) {
  const raw = String(nameOrUrl || '').trim().toLowerCase().split(/[?#]/)[0];
  const base = raw.includes('/') ? raw.slice(raw.lastIndexOf('/') + 1) : raw;
  return ['.pdf', '.png', '.jpg', '.jpeg'].find((ext) => base.endsWith(ext)) || '';
}

/**
 * 店铺证书库新建/编辑：选类型库规则 → 平台预转换文件 → 仅落本地模板（不提交平台）。
 */
export default function CertificateLocalSaveModal({
  open,
  mode,
  shopId,
  scope,
  typeOptions: parentTypeOptions,
  seed,
  onClose,
  onDone,
}) {
  const { t } = useTranslation('workbench');
  const fileInputRef = useRef(null);
  const needInputRefs = useRef({});
  const [typeId, setTypeId] = useState('');
  const [typeCode, setTypeCode] = useState('');
  const [typeName, setTypeName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [name, setName] = useState('');
  const [remark, setRemark] = useState('');
  const [fileName, setFileName] = useState('');
  const [productId, setProductId] = useState('');
  const [typeOptions, setTypeOptions] = useState([]);
  const [needItems, setNeedItems] = useState([]);
  const [needUploads, setNeedUploads] = useState({});
  const [files, setFiles] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [syncingTypes, setSyncingTypes] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const isEdit = mode === 'edit';
  const busy = saving || uploading || syncingTypes || typesLoading;
  const title = isEdit
    ? t('certForm.editTitle', { name: seed?.poolSn || seed?.name || '' })
    : t('certForm.createTitle');

  const mergedTypeOptions = useMemo(() => {
    if (typeOptions.length) return typeOptions;
    return Array.isArray(parentTypeOptions) ? parentTypeOptions : [];
  }, [typeOptions, parentTypeOptions]);

  const typeSelectOptions = useMemo(
    () => mergedTypeOptions.map((item) => {
      const code = typeCodeOf(item);
      const label = typeOptionLabel(item);
      return {
        value: String(item.certificateTypeId),
        label: code ? `${label}（${code}）` : label,
        code,
        name: label,
        needUploadItems: parseNeedUploadItems(item),
        raw: item,
      };
    }),
    [mergedTypeOptions],
  );

  const applyTypeRules = useCallback((typeRow, seedRow) => {
    const items = parseNeedUploadItems(typeRow);
    setNeedItems(items);
    const seedFiles = Array.isArray(seedRow?.fileList)
      ? seedRow.fileList
      : (Array.isArray(seedRow?.payload?.fileList) ? seedRow.payload.fileList : []);
    if (!items.length) {
      setNeedUploads({});
      setFiles((seedFiles || []).filter((f) => f?.fileUrl).map((f) => ({
        fileName: f.fileName || 'certificate',
        fileUrl: f.fileUrl,
        fileMd5: f.fileMd5 || '',
        imageUrl: f.imageUrl || '',
        imageMd5: f.imageMd5 || '',
      })));
      return;
    }
    const next = {};
    (seedFiles || []).forEach((f, idx) => {
      if (!f?.fileUrl && !f?.imageUrl) return;
      const key = `${Number(items[idx]?.itemType) || 0}-${idx}`;
      next[key] = {
        fileName: f.fileName || 'file',
        fileUrl: f.fileUrl || f.imageUrl,
        fileMd5: f.fileMd5 || '',
        imageUrl: f.imageUrl || '',
        imageMd5: f.imageMd5 || '',
      };
    });
    setNeedUploads(next);
    setFiles([]);
  }, []);

  useEffect(() => {
    if (!open || !shopId) return undefined;
    let cancelled = false;
    const seedTypeId = seed?.certificateTypeId != null ? String(seed.certificateTypeId) : '';
    setTypeId(seedTypeId);
    setTypeCode(String(seed?.certificateTypeCode || '').trim());
    setTypeName(String(seed?.certificateTypeName || '').trim());
    setStyleNo(String(seed?.styleNo || '').trim());
    setName(String(seed?.name || seed?.fileName || '').trim());
    setRemark(String(seed?.remark || '').trim());
    setFileName(String(seed?.fileName || '').trim());
    setProductId(seed?.productId != null ? String(seed.productId) : '');
    setNeedItems([]);
    setNeedUploads({});
    setFiles([]);
    setDragOver(false);
    setUploading(false);
    setSaving(false);
    setSyncingTypes(false);
    setTypesLoading(true);

    temuPlatformAPI.listCertificateTypes(shopId)
      .then((res) => {
        if (cancelled) return;
        const data = unwrapApiData(res);
        const types = parseTypeOptions(data);
        setTypeOptions(types);
        if (seedTypeId) {
          const hit = types.find((t) => String(t.certificateTypeId) === String(seedTypeId));
          if (hit) {
            setTypeCode(typeCodeOf(hit) || String(seed?.certificateTypeCode || ''));
            setTypeName(typeOptionLabel(hit));
            applyTypeRules(hit, seed);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setTypeOptions([]);
      })
      .finally(() => {
        if (!cancelled) setTypesLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, shopId, seed, applyTypeRules]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  const onTypeChange = (id) => {
    const hit = typeSelectOptions.find((opt) => String(opt.value) === String(id));
    setTypeId(id ? String(id) : '');
    setTypeCode(hit?.code || '');
    setTypeName(hit?.name || '');
    applyTypeRules(hit?.raw || hit || {}, null);
  };

  const syncTypes = async () => {
    if (!shopId || syncingTypes) return;
    setSyncingTypes(true);
    try {
      const pid = Number(productId);
      const res = await temuPlatformAPI.syncCertificateTypes(shopId, {
        ...(Number.isFinite(pid) && pid > 0 ? { productId: pid } : {}),
      });
      const data = unwrapApiData(res);
      const types = parseTypeOptions(data);
      setTypeOptions(types);
      const count = Number(data?.synced ?? res?.synced ?? types.length) || types.length;
      showToast(count > 0 ? t('certForm.syncedTypes', { count }) : t('certForm.noTypes'), count > 0 ? 'success' : 'warning');
      if (typeId) {
        const hit = types.find((t) => String(t.certificateTypeId) === String(typeId));
        if (hit) applyTypeRules(hit, seed);
      }
    } catch (e) {
      showToast(e?.message || t('certForm.syncTypesFailed'), 'error');
    } finally {
      setSyncingTypes(false);
    }
  };

  const convertAndAttach = async (file, item) => {
    const asImage = isImageNeedItem(item);
    const base64 = await fileToBase64(file);
    if (asImage) {
      const res = await temuPlatformAPI.uploadCompliancePhotoFile(shopId, {
        image: base64,
        clientId: `cert-lib-${Date.now()}`,
      });
      const imageUrl = res?.result?.imageUrl || res?.imageUrl || res?.result?.url || res?.url || '';
      const imageMd5 = res?.result?.imageMd5 || res?.imageMd5 || res?.result?.md5 || res?.md5 || '';
      if (!imageUrl) throw new Error(t('certForm.imageNoUrl'));
      return {
        fileName: file.name,
        fileUrl: imageUrl,
        imageUrl,
        imageMd5,
        fileMd5: imageMd5,
      };
    }
    const res = await temuPlatformAPI.uploadComplianceCertificateFile(shopId, { base64File: base64 });
    const fileUrl = res?.result?.url || res?.url || '';
    const fileMd5 = res?.result?.md5 || res?.md5 || res?.result?.fileMd5 || '';
    if (!fileUrl) throw new Error(t('certForm.fileNoUrl'));
    return { fileName: file.name, fileUrl, fileMd5 };
  };

  const onNeedFilePick = async (itemKey, item, file) => {
    if (!shopId || !file || busy) return;
    if (!isAllowedCertFile(file)) {
      showToast(t('certForm.unsupported', { name: file.name }), 'error');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      showToast(t('certForm.tooBig', { name: file.name }), 'error');
      return;
    }
    setNeedUploads((prev) => ({
      ...prev,
      [itemKey]: { ...(prev[itemKey] || {}), uploading: true, fileName: file.name },
    }));
    setUploading(true);
    try {
      const uploaded = await convertAndAttach(file, item);
      setNeedUploads((prev) => ({
        ...prev,
        [itemKey]: { ...uploaded, uploading: false },
      }));
      if (!fileName) setFileName(String(file.name || '').replace(/\.[^.]+$/, ''));
      showToast(t('certForm.convertedPlatform', { name: file.name }), 'success');
    } catch (e) {
      setNeedUploads((prev) => ({
        ...prev,
        [itemKey]: { ...(prev[itemKey] || {}), uploading: false },
      }));
      showToast(e?.message || t('certForm.convertFailed'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const uploadLooseFiles = async (fileList) => {
    if (!shopId || !fileList?.length || busy) return;
    const accepted = [];
    for (const file of fileList) {
      if (!isAllowedCertFile(file)) {
        showToast(t('certForm.unsupported', { name: file.name }), 'error');
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        showToast(t('certForm.tooBig', { name: file.name }), 'error');
        continue;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of accepted) {
        uploaded.push(await convertAndAttach(file, { itemType: 1 }));
      }
      setFiles((prev) => {
        const next = [...prev, ...uploaded];
        if (!String(fileName || '').trim() && next[0]?.fileName) {
          setFileName(String(next[0].fileName).replace(/\.[^.]+$/, ''));
        }
        return next;
      });
      showToast(t('certForm.convertedMany', { count: uploaded.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('certForm.fileUploadFailed'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const collectFileList = () => {
    if (needItems.length) {
      return needItems.map((item, idx) => {
        const key = `${Number(item?.itemType) || 0}-${idx}`;
        const u = needUploads[key];
        if (!u?.fileUrl && !u?.imageUrl) {
          if (item?.required) throw new Error(t('certForm.needItem', { name: item.itemName || item.name || t('certForm.pendingItem') }));
          return null;
        }
        return {
          fileName: u.fileName || item.itemName || 'file',
          fileUrl: u.fileUrl || u.imageUrl,
          ...(u.fileMd5 ? { fileMd5: u.fileMd5 } : {}),
          ...(u.imageUrl ? { imageUrl: u.imageUrl } : {}),
          ...(u.imageMd5 ? { imageMd5: u.imageMd5 } : {}),
          itemType: Number(item?.itemType) || undefined,
          itemName: item?.itemName || item?.name || undefined,
          required: Boolean(item?.required),
        };
      }).filter(Boolean);
    }
    if (!files.length) throw new Error(t('certForm.needOneFile'));
    return files.map((f, idx) => {
      const custom = String(fileName || '').trim();
      let resolved = f.fileName || `certificate_${idx + 1}.pdf`;
      if (idx === 0 && custom) {
        resolved = extractCertificateExt(custom)
          ? custom
          : `${custom}${extractCertificateExt(f.fileName) || '.pdf'}`;
      }
      return {
        fileName: resolved,
        fileUrl: f.fileUrl,
        ...(f.fileMd5 ? { fileMd5: f.fileMd5 } : {}),
      };
    });
  };

  const submit = async () => {
    if (!shopId) return;
    if (!typeId) {
      showToast(t('certForm.pickType'), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(t('photoLib.needStyleNo'), 'error');
      return;
    }
    const titleValue = String(name || '').trim() || String(fileName || '').trim() || t('certForm.unnamed');
    let fileListPayload;
    try {
      fileListPayload = collectFileList();
    } catch (e) {
      showToast(e?.message || t('certForm.needFiles'), 'error');
      return;
    }
    setSaving(true);
    try {
      const body = {
        id: isEdit && seed?.id ? seed.id : undefined,
        scope: scope || undefined,
        name: titleValue,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        certificateTypeId: Number(typeId),
        certificateTypeCode: typeCode || String(typeId),
        certificateTypeName: typeName || undefined,
        fileName: String(fileName || '').trim() || titleValue,
        fileList: fileListPayload,
        needUploadItems: needItems,
      };
      const res = await temuPlatformAPI.saveCertificateLocalTemplate(shopId, body);
      const item = res?.item || unwrapApiData(res)?.item || res;
      showToast(isEdit ? t('certForm.updatedLocal') : t('certForm.createdLocal'), 'success');
      onDone?.(item);
      onClose?.();
    } catch (e) {
      showToast(e?.message || t('certLib.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={() => { if (!busy) onClose?.(); }}
      />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(720px, calc(100vw - 32px))' }}
      >
        <div className="tpo-batch-modal__head">
          <h2>{title}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose} disabled={busy}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          <div
            className="tpo-cert-modal__toolbar"
            style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}
          >
            <button
              type="button"
              className="tpo-btn tpo-btn--secondary tpo-btn--sm"
              disabled={busy || !shopId}
              onClick={syncTypes}
            >
              {syncingTypes ? t('productAction.syncing') : t('certForm.syncTypes')}
            </button>
            <p className="tpo-muted" style={{ margin: 0 }}>
              {t('certForm.syncHint')}
            </p>
          </div>

          <div className="tpo-cert-modal__grid">
            <label className="tpo-field">
              <span className="tpo-field__label">{t('certForm.productId')}</span>
              <input
                className="tpo-input"
                value={productId}
                disabled={busy}
                placeholder={t('certForm.productIdPh')}
                onChange={(e) => setProductId(e.target.value)}
              />
            </label>
            <label className="tpo-field">
              <span className="tpo-field__label">
                {t('productTemplate.colStyleNo')}
                <RequiredMark />
              </span>
              <input
                className="tpo-input"
                value={styleNo}
                disabled={busy}
                maxLength={128}
                placeholder={t('certForm.stylePh')}
                onChange={(e) => setStyleNo(e.target.value)}
              />
            </label>
            <label className="tpo-field">
              <span className="tpo-field__label">
                {t('certForm.tplName')}
                <RequiredMark />
              </span>
              <input
                className="tpo-input"
                value={name}
                disabled={busy}
                maxLength={128}
                placeholder={t('certForm.tplNamePh')}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <div className="tpo-field">
              <span className="tpo-field__label">
                {t('certLib.colType')}
                <RequiredMark />
              </span>
              <SearchableSelect
                options={typeSelectOptions}
                value={typeId}
                disabled={isEdit || busy}
                placeholder={
                  typesLoading
                    ? t('certForm.loadingTypes')
                    : (typeSelectOptions.length ? t('certForm.pickType') : t('certForm.noTypesYet'))
                }
                emptyText={t('certForm.noMatchType')}
                onChange={onTypeChange}
              />
            </div>
            <label className="tpo-field">
              <span className="tpo-field__label">{t('certForm.fileNameLabel')}</span>
              <input
                className="tpo-input"
                value={fileName}
                disabled={busy}
                placeholder={t('certForm.fileNamePh')}
                onChange={(e) => setFileName(e.target.value)}
              />
            </label>
            <label className="tpo-field">
              <span className="tpo-field__label">{t('certForm.remark')}</span>
              <input
                className="tpo-input"
                value={remark}
                disabled={busy}
                maxLength={512}
                placeholder={t('photoLib.optional')}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
          </div>

          {typeCode ? (
            <p className="tpo-muted">
              {t('certForm.currentType')}
              <code>{typeName || typeCode}</code>
              {typeId ? ` · certType=${typeId}` : ''}
            </p>
          ) : null}

          {needItems.length > 0 ? (
            <div className="tpo-cert-need-list" style={{ display: 'grid', gap: 12, marginTop: 12 }}>
              <p className="tpo-muted" style={{ margin: 0 }}>
                {t('certForm.ruleHint')}
              </p>
              {needItems.map((item, idx) => {
                const key = `${Number(item?.itemType) || 0}-${idx}`;
                const uploaded = needUploads[key];
                const label = item?.itemName || item?.name || t('certForm.pendingN', { n: idx + 1 });
                return (
                  <div key={key} className="tpo-field tpo-field--full">
                    <span className="tpo-field__label">
                      {label}
                      {item?.required ? <RequiredMark /> : null}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                        disabled={busy}
                        onClick={() => needInputRefs.current[key]?.click()}
                      >
                        {uploaded?.uploading ? t('certForm.converting') : (uploaded?.fileUrl ? t('certForm.reupload') : t('certForm.pickAndConvert'))}
                      </button>
                      <input
                        ref={(el) => { needInputRefs.current[key] = el; }}
                        type="file"
                        accept={ACCEPT_FILES}
                        hidden
                        disabled={busy}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file) onNeedFilePick(key, item, file);
                        }}
                      />
                      {uploaded?.fileUrl ? (
                        <a href={uploaded.fileUrl} target="_blank" rel="noreferrer" className="tpo-hint-link">
                          {uploaded.fileName || t('certForm.convertedFileName')}
                        </a>
                      ) : (
                        <span className="tpo-muted">
                          {isImageNeedItem(item) ? t('certForm.imageVia') : t('certForm.fileVia')}
                        </span>
                      )}
                      {uploaded?.fileUrl ? (
                        <button
                          type="button"
                          className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                          disabled={busy}
                          onClick={() => setNeedUploads((prev) => {
                            const next = { ...prev };
                            delete next[key];
                            return next;
                          })}
                        >
                          {t('certForm.remove')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="tpo-field tpo-field--full tpo-cert-upload-field">
              <span className="tpo-field__label">
                {t('certForm.certFiles')}
                <RequiredMark />
              </span>
              <div
                className={`tpo-cert-dropzone${dragOver ? ' tpo-cert-dropzone--over' : ''}${uploading ? ' is-busy' : ''}`}
                onDragEnter={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
                onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  if (busy || !shopId) return;
                  uploadLooseFiles(Array.from(e.dataTransfer?.files || []));
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_FILES}
                  multiple
                  className="tpo-cert-dropzone__input"
                  disabled={busy || !shopId}
                  onChange={(e) => {
                    uploadLooseFiles(Array.from(e.target.files || []));
                    e.target.value = '';
                  }}
                />
                <span className="tpo-cert-dropzone__placeholder">
                  <span className="tpo-cert-dropzone__plus">+</span>
                  <span className="tpo-cert-dropzone__title">
                    {uploading ? t('certForm.convertingUpload') : t('certForm.dropHint')}
                  </span>
                  <span className="tpo-cert-dropzone__hint">
                    {t('certForm.formatHint')}
                  </span>
                </span>
              </div>
              {files.length > 0 ? (
                <ul className="tpo-cert-uploaded-list" aria-label={t('certForm.uploadedAria')}>
                  {files.map((f, idx) => (
                    <li key={`${f.fileMd5 || f.fileUrl || f.fileName}-${idx}`} className="tpo-cert-uploaded-item">
                      <FileText size={18} className="tpo-cert-uploaded-item__icon" aria-hidden="true" />
                      <span className="tpo-cert-uploaded-item__name" title={f.fileName}>
                        {f.fileName || t('certLib.unnamed')}
                      </span>
                      <button
                        type="button"
                        className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                        disabled={busy}
                        onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        {t('certForm.remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )}
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onClose} disabled={busy}>
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary tpo-btn--sm"
            onClick={submit}
            disabled={busy || typesLoading}
          >
            {saving ? t('tiktokPublish.saving') : (isEdit ? t('photoLib.saveChanges') : t('certForm.createLocal'))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
